/**
 * 图片压缩服务模块
 * 使用 browser-image-compression 在浏览器端压缩图片
 * @see Requirements 1.1, 1.2, 1.3
 */

export interface CompressionOptions {
  /** 目标文件大小上限 (MB)，默认 3.0 */
  maxSizeMB: number;
  /** 目标分辨率上限 (像素)，默认 2048 */
  maxWidthOrHeight: number;
  /** 最小边下限 (像素)，默认 1024 */
  minWidthOrHeight: number;
  /** 使用 Web Worker，默认 true */
  useWebWorker: boolean;
  /** 保留 EXIF，默认 false */
  preserveExif: boolean;
  /** 初始质量 (0-1)，默认 0.85 */
  initialQuality: number;
  /** 输出文件类型，默认 'image/jpeg' */
  fileType?: string;
  /** 压缩进度回调 */
  onProgress?: (progress: number) => void;
}

export interface CompressionResult {
  /** 压缩后的文件 */
  compressedFile: File;
  /** 原始文件大小 (bytes) */
  originalSize: number;
  /** 压缩后文件大小 (bytes) */
  compressedSize: number;
  /** 压缩比 (0-1) */
  compressionRatio: number;
  /** 压缩后宽度 */
  width: number;
  /** 压缩后高度 */
  height: number;
}

/** 默认压缩配置 */
export const DEFAULT_COMPRESSION_OPTIONS: CompressionOptions = {
  maxSizeMB: 3.0, // 提高到 3MB，确保 2048px 的 PNG 图片不会被过度压缩
  maxWidthOrHeight: 2048, // 保持 2048px，支持两阶段检测和 AI 诊断
  minWidthOrHeight: 1024,
  useWebWorker: true,
  preserveExif: false,
  initialQuality: 0.85, // 提高质量到 85%，减少压缩伪影
  fileType: "image/jpeg", // 强制转换为 JPEG，减小文件大小
};

/**
 * 获取图片尺寸
 */
async function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/**
 * 计算目标尺寸
 *
 * 策略：
 * 1. 如果图片已经小于等于目标尺寸，不缩放
 * 2. 否则，缩放到目标尺寸（保持宽高比）
 *
 * @param width 原始宽度
 * @param height 原始高度
 * @param maxWidthOrHeight 最大边上限
 * @returns 目标 maxWidthOrHeight 值
 */
function calculateTargetMaxDimension(
  width: number,
  height: number,
  maxWidthOrHeight: number,
  minWidthOrHeight: number,
): number {
  const maxDim = Math.max(width, height);

  // 如果图片已经小于等于目标尺寸，不缩放
  if (maxDim <= maxWidthOrHeight) {
    return maxDim;
  }

  // 否则，缩放到目标尺寸
  return maxWidthOrHeight;
}

/**
 * 判断图片是否需要压缩
 * @param file 原始文件
 * @param maxSizeMB 最大文件大小 (MB)
 * @param maxWidthOrHeight 最大分辨率
 */
export async function needsCompression(
  file: File,
  maxSizeMB: number = DEFAULT_COMPRESSION_OPTIONS.maxSizeMB,
  maxWidthOrHeight: number = DEFAULT_COMPRESSION_OPTIONS.maxWidthOrHeight,
): Promise<boolean> {
  // 检查文件大小
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return true;
  }

  // 检查图片尺寸
  try {
    const { width, height } = await getImageDimensions(file);
    if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
      return true;
    }
  } catch {
    // 无法获取尺寸时，保守起见进行压缩
    return true;
  }

  return false;
}

/**
 * 压缩图片
 * @param file 原始文件
 * @param options 压缩配置
 */
export async function compressImage(
  file: File,
  options?: Partial<CompressionOptions>,
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_COMPRESSION_OPTIONS, ...options };
  const originalSize = file.size;

  try {
    // 获取原始尺寸
    const { width, height } = await getImageDimensions(file);

    // 计算目标 maxWidthOrHeight，确保最小边不低于 minWidthOrHeight
    const targetMaxDim = calculateTargetMaxDimension(
      width,
      height,
      opts.maxWidthOrHeight,
      opts.minWidthOrHeight,
    );

    console.log("📐 压缩参数计算:", {
      原始尺寸: `${width} x ${height}`,
      原始最大边: Math.max(width, height),
      目标最大边: targetMaxDim,
      是否需要缩放: Math.max(width, height) > opts.maxWidthOrHeight,
    });

    // 动态导入 browser-image-compression，避免 Vite 静态解析问题
    const imageCompression = (await import("browser-image-compression"))
      .default;

    // 调用 browser-image-compression
    const compressedFile = await imageCompression(file, {
      maxSizeMB: opts.maxSizeMB,
      maxWidthOrHeight: targetMaxDim,
      useWebWorker: opts.useWebWorker,
      preserveExif: opts.preserveExif,
      initialQuality: opts.initialQuality,
      fileType: opts.fileType,
      onProgress: opts.onProgress,
    });

    // 获取压缩后的尺寸
    const compressedDims = await getImageDimensions(compressedFile);

    return {
      compressedFile,
      originalSize,
      compressedSize: compressedFile.size,
      compressionRatio: 1 - compressedFile.size / originalSize,
      width: compressedDims.width,
      height: compressedDims.height,
    };
  } catch (error) {
    // 压缩失败时返回原文件
    console.warn("Image compression failed, using original file:", error);
    const { width, height } = await getImageDimensions(file);
    return {
      compressedFile: file,
      originalSize,
      compressedSize: file.size,
      compressionRatio: 0,
      width,
      height,
    };
  }
}

/**
 * 格式化文件大小显示
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}
